// MqttContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import * as mqtt from "react-paho-mqtt";
import { useDispatch, useSelector } from "react-redux";
import AlertModal from "../components/Commands/Alerts/AlertModal";
import { List, notification } from "antd";
import {
  NotificationFormatter,
  NotificationFormatterNew,
  alertFormatter,
} from "../helperFuncs";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { updateEventModal } from "../redux/slice/userDetailSlice";
import AMC from "../components/Commands/AMC";
import { PowerStateModal } from "../components/Commands/Modals/PowerStateModal";
import { isWWWhostname } from "../utils/helpers";

const MqttContext = createContext();

export const MqttProvider = ({ children, clientId }) => {
  const [client, setClient] = useState(null);
  const [message, setMessage] = useState(null);
  const [connected, setConnected] = useState(false);
  const [responseType, setresponseType] = useState("");
  const [responsedata, setresponsedata] = useState("");
  const [showalert, setshowalert] = useState(false);

  const { username, eventmodal } = useSelector((state) => state.userDetails);
  const { name: domainname } = useSelector((state) => state.domain);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [lastAlert, setLastAlert] = useState(null);

  // Refs to prevent stale closures and manage reconnection
  const clientRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const isConnectingRef = useRef(false);
  const mountedRef = useRef(true);
  const reconnectAttemptsRef = useRef(0);
  const connectionInitializedRef = useRef(false); // Track if connection has been initialized
  const maxReconnectAttempts = 10;
  const baseReconnectDelay = 2000;

  // Store the client ID to reuse on reconnections
  const stableClientIdRef = useRef(null);
  // Store initial credentials to prevent reconnections on credential changes
  const initialCredentialsRef = useRef({ clientId: null, username: null });

  // Generate stable client ID once and reuse it
  const getStableClientId = useCallback(() => {
    if (!stableClientIdRef.current && clientId && username) {
      stableClientIdRef.current = `${clientId}_${username}_${Date.now()}`;
      // console.log("Generated stable client ID:", stableClientIdRef.current);
    }
    return stableClientIdRef.current;
  }, [clientId, username]);

  // Stable message handler using useCallback
  const onMessageArrived = useCallback(
    async (message) => {
      try {
        const { topic, payloadString } = message;
        let messagePayload = JSON.parse(payloadString);

        if (!mountedRef.current) return;

        setMessage(JSON.parse(payloadString));
        // console.log(messagePayload, "payload string");

        // ALERTS: alert come here
        if (topic === username) {
          if (eventmodal) {
            notification.success({
              message: "ALERT",
              description: (
                <>
                  <p>{alertFormatter(messagePayload?.alerttype).title}</p>
                  <p>{alertFormatter(messagePayload?.alerttype).caption}</p>
                  <p
                    role="button"
                    className="text-blue-600 underline"
                    onClick={() => {
                      navigate(
                        `/meter-details/${username}/${messagePayload?.deviceid}`
                      );
                    }}
                  >
                    {messagePayload?.deviceid}
                  </p>
                </>
              ),
            });
          } else {
            dispatch(updateEventModal(true));
            setresponsedata(messagePayload);
            setresponseType("ALERT");
          }
        } else if (topic === domainname + "/mqtt_" + username) {
          console.log(messagePayload, "message came from the broker");

          if (eventmodal) {
            if (
              messagePayload?.type === "TON" ||
              messagePayload?.type === "OFF"
            ) {
              notification.success({
                duration: 6,
                message: NotificationFormatterNew(messagePayload?.type)
                  ?.caption,
                description: (messagePayload?.type == "TON" ||
                  messagePayload?.type == "OFF") && (
                  <div>
                    {/failure@(NO_CREDIT_ERROR|SERVICE_EXPIRED_ERROR)/.test(
                      messagePayload?.status
                    ) ? (
                      <>
                        <p>
                          Request to{" "}
                          {
                            NotificationFormatterNew(messagePayload?.type)
                              .caption
                          }{" "}
                          meter failed.
                        </p>
                        <p>
                          <strong>Reason:</strong> No credit on meter, please
                          recharge.
                        </p>
                        <p
                          role="button"
                          className="text-blue-600 underline"
                          onClick={() => {
                            navigate(
                              `/meter-details/${username}/${messagePayload?.deviceid}`
                            );
                          }}
                        >
                          {messagePayload?.deviceid}
                        </p>
                      </>
                    ) : (
                      <>
                        <p>
                          {
                            NotificationFormatterNew(messagePayload?.type)
                              .description
                          }
                        </p>
                        <p
                          role="button"
                          className="text-blue-600 underline"
                          onClick={() => {
                            navigate(
                              `/meter-details/${username}/${messagePayload?.deviceid}`
                            );
                          }}
                        >
                          {messagePayload?.deviceid}
                        </p>
                      </>
                    )}
                  </div>
                ),
              });
            } else {
              notification.success({
                message: NotificationFormatterNew(messagePayload?.type).caption,
                description:
                  messagePayload?.type == "BEN" ||
                  messagePayload?.type == "LEN" ? (
                    <>
                      <List
                        header={
                          <strong>
                            Balance on meter ({messagePayload?.deviceid})
                          </strong>
                        }
                        bordered={false}
                        dataSource={messagePayload?.balances ?? []}
                        renderItem={(item) => (
                          <List.Item className="flex justify-between">
                            <p>{NotificationFormatter(item?.commandType)}</p>
                            <p>
                              {item?.value
                                ? item?.value
                                : item?.energyLimit
                                ? item?.energyLimit
                                : 0}
                            </p>
                          </List.Item>
                        )}
                      />
                    </>
                  ) : (
                    <>
                      <p>
                        {
                          NotificationFormatterNew(messagePayload?.type)
                            .description
                        }
                      </p>
                      {messagePayload?.type === "GPT" && (
                        <p>
                          {messagePayload?.status == 1
                            ? "Tamper is on meter"
                            : "Tamper is off on meter"}
                        </p>
                      )}
                      {messagePayload?.type === "SFE" && (
                        <p>
                          Your meter has experienced a power supply interruption{" "}
                          <span className="font-semibold">
                            {messagePayload?.status <= 1
                              ? "once"
                              : `${messagePayload?.status} times`}
                          </span>
                        </p>
                      )}
                      {messagePayload?.type === "SDE" && (
                        <p>
                          The total duration of the power supply interruption is{" "}
                          <span className="font-semibold">
                            {messagePayload?.status}{" "}
                            {messagePayload?.status === 1
                              ? "minute"
                              : "minutes"}
                          </span>
                        </p>
                      )}
                      <p
                        role="button"
                        className="text-blue-600 underline"
                        onClick={() => {
                          navigate(
                            `/meter-details/${username}/${messagePayload?.deviceid}`
                          );
                        }}
                      >
                        {messagePayload?.deviceid}
                      </p>
                    </>
                  ),
              });
            }
          } else {
            dispatch(updateEventModal(true));
            setresponsedata(messagePayload);
            setresponseType(messagePayload?.type);
          }
        }
      } catch (err) {
        console.error("Error processing MQTT message:", err.message);
      }
    },
    [username, domainname, eventmodal, navigate, dispatch]
  );

  // Stable connection lost handler - only handles actual disconnections
  const onConnectionLost = useCallback((responseObject) => {
    // console.log(
    //   "Connection lost:",
    //   responseObject.errorMessage,
    //   "Error code:",
    //   responseObject.errorCode
    // );
    setConnected(false);

    if (!mountedRef.current) return;

    // Only attempt reconnection if it's an actual network disconnection (not manual disconnect)
    if (responseObject.errorCode !== 0 && !isConnectingRef.current) {
      // console.log(
      //   "Actual connection lost detected, scheduling reconnection..."
      // );
      scheduleReconnect();
    } else {
      // console.log(
      //   "Manual disconnection or already connecting, skipping reconnection"
      // );
    }
  }, []);

  // Exponential backoff reconnection - only for actual disconnections
  const scheduleReconnect = useCallback(() => {
    if (
      !mountedRef.current ||
      isConnectingRef.current ||
      !connectionInitializedRef.current
    ) {
      // console.log(
      //   "Skipping reconnection: mounted:",
      //   mountedRef.current,
      //   "connecting:",
      //   isConnectingRef.current,
      //   "initialized:",
      //   connectionInitializedRef.current
      // );
      return;
    }

    // Clear any existing reconnection timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      // console.error("Maximum reconnection attempts reached");
      toast.error("MQTT connection failed. Please refresh the page.");
      return;
    }

    const delay = Math.min(
      baseReconnectDelay * Math.pow(1.5, reconnectAttemptsRef.current),
      30000
    );

    // console.log(
    //   `Scheduling reconnection attempt ${
    //     reconnectAttemptsRef.current + 1
    //   } in ${delay}ms using client ID: ${getStableClientId()}`
    // );

    reconnectTimeoutRef.current = setTimeout(() => {
      if (
        mountedRef.current &&
        !isConnectingRef.current &&
        connectionInitializedRef.current
      ) {
        reconnectAttemptsRef.current++;
        performConnection();
      }
    }, delay);
  }, [getStableClientId]);

  // Extracted connection logic - stable reference
  const performConnection = useCallback(() => {
    if (!clientId || !username || isConnectingRef.current) {
      // console.log("Cannot connect: missing credentials or already connecting");
      return;
    }

    isConnectingRef.current = true;
    // console.log("Performing MQTT connection...");

    try {
      // Clean up existing client
      if (clientRef.current) {
        try {
          if (clientRef.current.isConnected()) {
            clientRef.current.disconnect();
          }
        } catch (err) {
          console.warn("Error disconnecting previous client:", err);
        }
        clientRef.current = null;
      }

      const hostname =
        isWWWhostname() === "xyz"
          ? "staging.broker.chanels.io"
          : "broker.chanels.io";

      const stableClientId = getStableClientId();
      // console.log(`Connecting with client ID: ${stableClientId}`);

      const mqttClient = mqtt.connect(
        hostname,
        Number(8084),
        stableClientId,
        onConnectionLost,
        onMessageArrived
      );

      mqttClient.connect({
        onSuccess: () => {
          if (!mountedRef.current) {
            isConnectingRef.current = false;
            return;
          }

          // console.log(
          //   "MQTT connection successful with client ID:",
          //   stableClientId
          // );

          try {
            mqttClient.subscribe(username);
            mqttClient.subscribe(domainname + `/mqtt_${username}`);

            setConnected(true);
            setClient(mqttClient);
            clientRef.current = mqttClient;
            isConnectingRef.current = false;
            reconnectAttemptsRef.current = 0;
            connectionInitializedRef.current = true;

            // Clear any pending reconnection timeout
            if (reconnectTimeoutRef.current) {
              clearTimeout(reconnectTimeoutRef.current);
              reconnectTimeoutRef.current = null;
            }

            // console.log("MQTT subscription successful");
          } catch (subscribeError) {
            // console.error("Error subscribing to topics:", subscribeError);
            isConnectingRef.current = false;
          }
        },
        onFailure: (err) => {
          // console.error("MQTT connection failed:", err.errorMessage);
          isConnectingRef.current = false;
          setConnected(false);

          if (mountedRef.current) {
            scheduleReconnect();
          }
        },
        userName: "spiralbroker",
        password: process.env.REACT_APP_MQTT_PASSWORD,
        useSSL: true,
        keepAliveInterval: 30,
        timeout: 10,
        cleanSession: false,
        reconnect: false,
      });
    } catch (err) {
      // console.error("Error creating MQTT client:", err);
      isConnectingRef.current = false;
      if (mountedRef.current) {
        scheduleReconnect();
      }
    }
  }, [
    clientId,
    username,
    domainname,
    getStableClientId,
    onConnectionLost,
    onMessageArrived,
    scheduleReconnect,
  ]);

  // Initialize connection ONLY ONCE when provider mounts
  useEffect(() => {
    mountedRef.current = true;

    // Only initialize connection if we have credentials and haven't initialized before
    if (
      clientId &&
      username &&
      !connectionInitializedRef.current &&
      !isConnectingRef.current
    ) {
      // console.log("Initializing MQTT connection for the first time...");
      // Store initial credentials
      initialCredentialsRef.current = { clientId, username };
      performConnection();
    }

    // Cleanup function
    return () => {
      // console.log("MqttProvider cleanup...");
      mountedRef.current = false;
      isConnectingRef.current = false;

      // Clear reconnection timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Disconnect client
      if (clientRef.current) {
        try {
          if (clientRef.current.isConnected()) {
            console.log("Disconnecting MQTT client...");
            clientRef.current.disconnect();
          }
        } catch (err) {
          console.warn("Error disconnecting on cleanup:", err);
        }
        clientRef.current = null;
      }

      // Reset all state
      setConnected(false);
      setClient(null);
      connectionInitializedRef.current = false;
      stableClientIdRef.current = null;
      reconnectAttemptsRef.current = 0;
      initialCredentialsRef.current = { clientId: null, username: null };
    };
  }, []); // Empty dependency array - only run once on mount

  // Handle credential changes (if user logs out and logs back in)
  useEffect(() => {
    const credentialsChanged =
      initialCredentialsRef.current.clientId !== clientId ||
      initialCredentialsRef.current.username !== username;

    if (credentialsChanged && connectionInitializedRef.current) {
      // console.log("Credentials changed, reinitializing connection...");
      // Reset everything and reconnect with new credentials
      connectionInitializedRef.current = false;
      stableClientIdRef.current = null;
      reconnectAttemptsRef.current = 0;
      initialCredentialsRef.current = { clientId, username };

      if (clientId && username) {
        performConnection();
      }
    }
  }, [clientId, username, performConnection]);

  return (
    <MqttContext.Provider
      value={{
        client,
        message,
        connected,
        reconnectAttempts: reconnectAttemptsRef.current,
        connectionInitialized: connectionInitializedRef.current,
      }}
    >
      {eventmodal && responseType === "ALERT" && (
        <AlertModal
          responsedata={responsedata}
          setresponsedata={setresponsedata}
          open={eventmodal}
          setopen={() => {
            dispatch(updateEventModal(false));
            setresponsedata(null);
          }}
        />
      )}
      {children}
    </MqttContext.Provider>
  );
};

export const useMQTT = () => useContext(MqttContext);
